<template>
    <div class="audits-sections-and-items" :class="{ 'opened-info': infoIsOpen }">
        <!-- SECTIONS NAVBAR -->
        <div class="template-sections" v-if="hasSections" v-show="shouldShowSectionsNavbar">
            <TemplateSections :isAudit="true" :sections="sections" @element-click="handleMenuBarSectionClick" :doneSectionsIds="doneSectionsIds" :firstNotDoneSectionId="firstNotDoneSectionId" :skip-scroll-to-first-pending-section="skipScrollToFirstPendingSection" />
        </div>
        <!-- PROGRESS BAR -->
        <div class="progress-bar-section" v-if="checklist">
            <ProgressBar :itemsCompleted="checklist.itemsDone" :itemsTotal="checklist.total" />
        </div>
        <!-- SEARCH BAR -->
        <div class="sections-searchbar-container" v-if="showSearchBar" v-show="shouldShowSearchBar">
            <SearchBar :search="searchValue" @on-search="handleSearch" @on-button-click="handleClearSearch" :placeholder="$t('detail.sections_search.placeholder')" />
        </div>

        <!-- SECTIONS AND ITEMS -->

        <div class="detail-box" :class="detailBoxClasses">
            <div class="exam performing" id="exam" :class="[{ 'hide-scrollbar': ['portrait'].includes($mq) }, { scrollbar: ['landscape', 'desktop'].includes($mq) }]">
                <div class="note-detail">
                    <NoteDetail v-if="notes && notes.length > 0" :notes="notes" />
                </div>

                <div class="section minimize">
                    <div v-if="sectionsAreOpen" class="section-title">
                        <button class="section-button" @click.self="toggleAllSections('collapse')"></button>
                        <span>{{ $t('audit.actions.collapse_all') }}</span>
                    </div>
                    <div v-else class="section-title expand">
                        <button class="section-button" @click.self="toggleAllSections('expand')"></button>
                        <span>{{ $t('audit.actions.expand_all') }}</span>
                    </div>
                </div>

                <SimpleToast v-if="checklist.description" :key="checklist.id" :text="checklist.description" :includeToggleButton="true" type="checklist-description" :parse="true" />

                <div v-if="checklist.recover && !checklist.complete_date" class="get-last-values">
                    <span class="message" @click="$emit('recover-values')">{{ $t('tasks.get_last_values') }}</span>
                </div>

                <template v-if="checklist && sections">
                    <template v-if="showNoResultsEmptyState">
                        <EmptyPage :description="searchNoResultsMessage" />
                    </template>
                    <template v-else>
                        <!-- SECTIONS -->
                        <div v-for="section in sectionsToShow" :key="section.section_id" class="section" :class="{ closed: closedSections[section.section_id] }" :id="section.section_id">
                            <!-- SECTION TITLE -->
                            <div
                                class="section-title"
                                :class="[
                                    {
                                        'expand-full': !((templateConfig && templateConfig.show_score == undefined) || (templateConfig && templateConfig.show_score == 1 && valueInPercentage(section.completed, section.total_score) !== 0))
                                    }
                                ]"
                                :id="section.name"
                                :openThisSection="openThisSection(section.section_id)"
                                @click="toggleShowHideSectionItems(section.section_id)"
                            >
                                <!-- SECTION BUTTON (para abrir y cerrar la sección)-->
                                <div class="section-button-container">
                                    <button class="section-button"></button>
                                </div>

                                <div class="section-name-container">
                                    <div class="section-name">
                                        <!-- SECTION PROGRESS INDICATOR (completed or not completed section) -->
                                        <div class="section-progress-indicator">
                                            <div v-if="sectionItemsAreDone(section.section_id)" class="icon-container">
                                                <i class="i-andy-menu-checklist i-andy-m"></i>
                                            </div>
                                            <div v-else class="icon-container warning">
                                                <i class="i-andy-circle-exclamation i-andy-m"></i>
                                            </div>
                                        </div>
                                        <span class="section-name-text">{{ section.name }}</span>
                                        <div class="section-right-buttons-container">
                                            <span class="section-score" :class="scoreClasses(section.score)" v-if="(templateConfig && templateConfig.show_score == undefined) || (templateConfig && templateConfig.show_score == 1 && valueInPercentage(section.completed, section.total_score) !== 0)">
                                                <span class="percentage" v-if="valueInPercentage(section.completed, section.total_score) !== 0">
                                                    {{ ' ' + valueInPercentage(section.completed, section.total_score) }}
                                                </span>
                                            </span>
                                            <button v-if="sectionHasOptionalItems(section)" class="see-more-button" @click="toggleSeeMoreBox($event, section.section_id)">
                                                <i class="i-andy-more i-andy-m"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <!-- SEE MORE BOX -->
                                    <div v-if="showSeeMoreBox[section.section_id] && sectionHasOptionalItems(section)" class="see-more-box">
                                        <button v-if="allSectionItemsApply(section)" @click.stop="handleApplicability(ACTION_TYPE.NOT_APPLY, section)" class="apply-button no-apply">
                                            <i class="i-andy-error i-andy-m"></i>
                                            <span>{{ $t('answer.actions.no_apply') }}</span>
                                        </button>
                                        <button v-else @click.stop="handleApplicability(ACTION_TYPE.APPLY, section)" class="apply-button apply">
                                            <i class="i-andy-convert i-andy-sm"></i>
                                            {{ $t('answer.actions.make_apply') }}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- ITEMS -->
                            <div class="section-content" v-show="!closedSections[section.section_id]">
                                <answer v-for="item in section.items" :item="item" :list="checklist" :cId="checklist.id" :aId="item.item_id" :cType="type" :key="item.item_id" :showErrors="showErrors" :hasSubitemsActionPLan="item.subitems && item.subitems.length > 0" />
                            </div>
                        </div>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { formatPercent } from '@/utils/numberFormat'
import TemplateSections from '@/components/layout/TemplateSections'
import answer from '@/components/domain/tasks/answer'
import NoteDetail from '../../layout/NoteDetail.vue'
import SimpleToast from '@/components/layout/SimpleToast.vue'
import ProgressBar from '@/components/domain/ui/ProgressBar.vue'
import SearchBar from '@/components/domain/ui/SearchBar'
import EmptyPage from '@/components/layout/EmptyPage.vue'
import { ACTION_TYPE } from '@/constants'

export default {
    components: { TemplateSections, answer, NoteDetail, ProgressBar, SearchBar, EmptyPage, SimpleToast },
    name: 'SectionsAndItems',
    props: {
        sections: Array,
        checklist: Object,
        infoIsOpen: Boolean,
        templateConfig: Object,
        type: Number | String,
        showErrors: Boolean,
        itemsWithActionPlansQuantity: Number,
        notes: Array,
        doneSectionsIds: { type: Array, default: () => [] },
        skipScrollToFirstPendingSection: { type: Boolean, default: false }
    },
    data() {
        return {
            closedSections: {},
            searchValue: '',
            searchResult: [],
            showSeeMoreBox: {},
            ACTION_TYPE: ACTION_TYPE
        }
    },
    computed: {
        isUserRoleLevelHigherThanOne() {
            if (!this.userData || !this.userData.rol || !this.roles) {
                return false
            }

            const userRole = this.roles[this.userData.rol]

            if (!userRole || typeof userRole.level !== 'number') {
                return false
            }

            return userRole.level >= 2
        },
        sectionsToShow() {
            return this.searchValue && this.searchValue.length > 0 && this.searchResult && this.searchResult.length > 0 ? this.searchResult : this.sections
        },
        showSearchBar() {
            return this.hasSections && this.sections.length > 5
        },
        shouldShowSearchBar() {
            const isMobile = this.$mq === 'portrait'
            const isUserTyping = this.$store.getters['tasks/isUserTyping']
            // No mostrar search bar mientras es móvil y el usuario escribe en un input de texto
            if (isMobile && isUserTyping) return false
            return this.showSearchBar
        },
        hasSections() {
            return this.sections && this.sections.length > 1
        },
        sectionsAreOpen() {
            return Object.values(this.closedSections).filter((s) => s).length != Object.keys(this.checklist.sections).length
        },
        firstNotDoneSectionId() {
            if (!this.checklist.sections || !this.doneSectionsIds) {
                return null
            }

            const allSectionsIds = Object.keys(this.checklist.sections)

            return allSectionsIds.find((sectionId) => !this.doneSectionsIds.includes(sectionId))
        },
        detailBoxClasses() {
            return [
                {
                    'with-sections': this.hasSections,
                    'opened-info': this.infoIsOpen,
                    'with-search-bar': this.showSearchBar
                },
                'hide-scrollbar'
            ]
        },
        showNoResultsEmptyState() {
            return this.hasSections && this.searchValue && this.searchValue.length > 0 && this.searchResult && this.searchResult.length === 0
        },
        searchNoResultsMessage() {
            return this.$t('empty_pages.sections_not_found', { searchInput: this.searchValue })
        },
        loggedUserId() {
            return this.$store.getters['loginUser/getLocalEmployee']
        },
        userData() {
            if (this.loggedUserId) {
                return this.$store.getters['employee/getEmployee'](this.loggedUserId)
            }
            return false
        },
        roles() {
            return this.$store.getters['employee/getAllRoles']
        },
        typename() {
            if (this.type == 1) return 'tasks'
            if (this.type == 2) return 'audit'
            if (this.type == 3) return 'sensor'
            if (this.type == 4) return 'issues'
            if (this.type == 5) return 'audit'
            return 'tasks'
        },
        shouldShowSectionsNavbar() {
            const isMobile = this.$mq === 'portrait'
            const isUserTyping = this.$store.getters['tasks/isUserTyping']
            // No mostrar sections navbar mientras es móvil y el usuario escribe en un input de texto
            if (isMobile && isUserTyping) return false
            return this.hasSections
        }
    },
    methods: {
        formatDisplayPercent(value, level = 1, allowNegative = false) { return formatPercent(value, this.$i18n.locale, level, { allowNegative }) },
        sectionHasOptionalItems(sectionObject) {
            if (!sectionObject) {
                return false
            }

            const sectionItems = Object.values(sectionObject.items)

            return sectionItems.some((item) => item.mandatory == false)
        },
        allSectionItemsApply(sectionObject) {
            if (!sectionObject) {
                return true
            }

            const sectionItems = Object.values(sectionObject.items)

            return sectionItems.every((item) => item.apply == 1)
        },
        toggleSeeMoreBox(event, sectionId) {
            if (event) {
                event.stopPropagation()
            }

            this.showSeeMoreBox = {
                ...this.showSeeMoreBox,
                [sectionId]: !this.showSeeMoreBox[sectionId]
            }
        },
        /**
         @param {Number} actionType = Apply value (0 or 1 when 0 is not apply and 1 is apply)
         @returns {Object} Store dispatch data object for apply/not apply dispatch actions: this.typename + '/apply, this.typename + '/applyrecurrent
         */
        applyAndNotApplyDispatchData(actionType, sectionId) {
            // actionType === 1 => apply
            // actionType === 0 => not apply
            return {
                cType: this.type,
                check: this.checklist.id,
                answer: null,
                sectionId: sectionId,
                emp: this.loggedUserId,
                date: moment().unix(),
                apply: actionType,
                checkRef: null,
                itemRef: null
            }
        },
        // Maneja el "No aplica" y el "Realizar (aplica)" de los items de una misma sección
        handleApplicability(actionType, section) {
            const self = this

            if (self.isUserRoleLevelHigherThanOne) {
                // Popup para NO APLICA (actionType == 0)
                if (actionType == ACTION_TYPE.NOT_APPLY) {
                    self.$snackbar.hide()
                    this.showNotApplyConfirmPopup(section)
                } else {
                    // Popup para APLICA (actionType == 1)
                    self.showApplyConfirmPopup(section)
                }
            }
        },
        // Shows the apply confirm popup that shows 3 options: 1) not apply only for now OR, 2) not apply recurrently OR, 3) cancel (close popup)
        showApplyConfirmPopup(section) {
            const self = this

            const isOfflineActive = !fnCheckConnection() ? { customClass: 'offline' } : {}

            const popupMessage = self.$t('answer.apply.remember_apply_section_text_', {
                username: self.userData.name,
                task: section.name
            })

            self.$popup.confirm({
                ...isOfflineActive,
                message: popupMessage,
                textSave: self.$t('popup.confirm.not_apply_text_save'),
                textCancel: self.$t('popup.confirm.not_apply_text_cancel'),
                textCancel2: self.$t('popup.confirm.text_cancel'),
                callSave: () => self.handleApplyRecurrent(section),
                callCancel: (popupSelf, store, item) => self.handleApply(popupSelf, store, item, section),
                callCancel2: self.cancelApply
            })
        },
        // sends dispatch to set an item or a section as "applies" (actionType == 1)
        handleApply(ref, store, item, section) {
            const self = this

            if (item == 'button') {
                self.$overlay.loading()

                const dispatchData = self.applyAndNotApplyDispatchData(ACTION_TYPE.APPLY, section.section_id)

                self.$store
                    .dispatch(self.typename + '/apply', dispatchData)
                    .then((response) => {
                        self.$popup.close()
                        self.$overlay.hide()

                        const snackbarData = {
                            message: self.$t('answer.apply.revert_section'),
                            duration: 5000,
                            closeable: true,
                            type: 'info'
                        }

                        self.$snackbar.main(snackbarData)
                        self.showSeeMoreBox = {
                            ...self.showSeeMoreBox,
                            [section.section_id]: false
                        }
                    })
                    .catch((error) => {
                        logError(error)

                        self.$popup.close()
                        self.$overlay.hide()

                        const snackbarData = {
                            message: self.$t('answer.apply.revert_section'),
                            duration: 5000,
                            closeable: true,
                            type: 'info'
                        }

                        self.$snackbar.main(snackbarData)
                        self.showSeeMoreBox = {
                            ...self.showSeeMoreBox,
                            [section.section_id]: false
                        }
                    })
                // clicked outside
            } else {
                self.$popup.close()
            }
        },
        handleApplyRecurrent(section) {
            const self = this

            self.$overlay.loading()

            const dispatchData = self.applyAndNotApplyDispatchData(ACTION_TYPE.APPLY, section.section_id)

            self.$store
                .dispatch(self.typename + '/applyrecurrent', dispatchData)
                .then(function (response) {
                    self.$popup.close()
                    self.$overlay.hide()
                    self.showSeeMoreBox = {
                        ...self.showSeeMoreBox,
                        [section.section_id]: false
                    }
                })
                .catch((error) => {
                    logError(error)
                    self.$overlay.hide()
                })
        },
        cancelApply() {
            const self = this

            self.$popup.close()
            self.$overlay.hide()
            self.showSeeMoreBox = {}
        },
        // Shows the not apply confirm popup that shows 3 options: 1) not apply only for now OR, 2) not apply recurrently OR, 3) cancel (close popup)
        showNotApplyConfirmPopup(section) {
            const notApplyConfirmPopupMsg = this.$t('answer.apply.remember_no_apply_section_text', {
                username: this.userData.name,
                task: section.name
            })

            let confirmPopupData = {
                message: notApplyConfirmPopupMsg,
                textSave: this.$t('popup.confirm.not_apply_text_save'),
                textCancel: this.$t('popup.confirm.not_apply_text_cancel'),
                textCancel2: this.$t('popup.confirm.text_cancel'),
                classSave: 'act save',
                callSave: (popupSelf, store, item) => this.showReconfirmNotApplyRecurrentPopup(popupSelf, store, item, section),
                callCancel: (popupSelf, store, item) => this.handleNotApply(popupSelf, store, item, section),
                callCancel2: this.cancelNotApply
            }

            if (!fnCheckConnection()) {
                confirmPopupData = {
                    message: notApplyConfirmPopupMsg,
                    textSave: this.$t('popup.confirm.not_apply_text_cancel'),
                    callSave: (popupSelf, store, item) => this.handleNotApply(popupSelf, store, item, section)
                }
            }

            this.$popup.confirm(confirmPopupData)
        },
        // Shows the reconfirmation popup for not apply recurrentaction
        async showReconfirmNotApplyRecurrentPopup(popupSelf, store, item, section) {
            // close "confirm" modal in order to open "reconfirm" modal:
            await this.$popup.close()

            if (fnCheckConnection()) {
                const reconfirmPopupData = {
                    message: this.$t('answer.apply.confirm_not_apply'),
                    callSave: () => this.handleNotApplyRecurrent(section),
                    // cancel button has to close confirm recurrent modal and open again not apply confirm modal
                    callCancel: async () => {
                        // close this confirm recurrent modal and open again not apply confirm modal
                        await this.$popup.close()
                        this.showNotApplyConfirmPopup(section)
                    }
                }
                this.$popup.reconfirm(reconfirmPopupData)
            } else {
                this.$popup.alert({
                    closable: false,
                    blocking: false,
                    message: this.$t('empty_pages.connection_description'),
                    textSave: this.$t('audit.error.alert_save'),
                    callSave: () => {
                        this.$popup.close()
                    }
                })
            }
        },
        // Applies not apply "only for now" (temporary not apply)
        handleNotApply(ref, store, item, section) {
            if (fnCheckConnection() || this.$store.getters.getOffline) {
                if (item == 'button' || typeof item === 'undefined') {
                    this.$overlay.loading()

                    this.$store
                        .dispatch(this.typename + '/apply', this.applyAndNotApplyDispatchData(ACTION_TYPE.NOT_APPLY, section.section_id))
                        .then((response) => {
                            this.$popup.close()
                            this.$overlay.hide()
                            this.showSeeMoreBox = {
                                ...this.showSeeMoreBox,
                                [section.section_id]: false
                            }
                        })
                        .catch((error) => {
                            logError(error)
                            this.$overlay.hide()
                        })
                    // clicked outside
                } else {
                    this.$popup.close()
                }
            } else {
                this.$popup.close()
                this.$overlay.hide()
                this.$snackbar.error({
                    duration: 10000,
                    closeable: true,
                    message: this.$t('empty_pages.connection_description')
                })
            }
        },
        // Handles the remember not apply action (permanent not apply)
        handleNotApplyRecurrent(section) {
            const self = this

            if (fnCheckConnection()) {
                self.$overlay.loading()
                self.$store
                    .dispatch(self.typename + '/applyrecurrent', self.applyAndNotApplyDispatchData(ACTION_TYPE.NOT_APPLY, section.section_id))
                    .then((response) => {
                        self.$popup.close()
                        self.$overlay.hide()
                        self.showSeeMoreBox = false
                    })
                    .catch((error) => {
                        logError(error)
                        self.$overlay.hide()
                    })
            } else {
                self.$popup.close()
                self.$overlay.hide()
                self.$snackbar.error({
                    duration: 10000,
                    closeable: true,
                    message: self.$t('empty_pages.connection_description')
                })
            }
        },
        // Cancels the not apply action
        cancelNotApply(ref, store, item) {
            this.$popup.close()
            this.$overlay.hide()
            this.showSeeMoreBox = false
        },
        handleSearch(event) {
            if (event && event.target.value) {
                this.timerSearch = setTimeout(() => {
                    this.searchValue = event.target.value
                }, 300)
            } else {
                this.searchValue = ''
            }
        },
        handleClearSearch() {
            this.searchValue = ''
        },
        sectionItemsAreDone(sectionId) {
            return this.doneSectionsIds.includes(sectionId)
        },
        valueInPercentage(value, total) {
            const result = (value / total) * 100

            if (isNaN(result)) {
                return 0
            }

            return this.formatDisplayPercent(result)
        },
        scoreClasses(value) {
            return {
                success: value == 100,
                progress: value > 75 && value < 100,
                warning: value > 0 && value <= 75,
                error: value == 0
            }
        },
        toggleShowHideSectionItems(sec_id) {
            this.closedSections = {
                ...this.closedSections,
                [sec_id]: !this.closedSections[sec_id]
            }
        },
        openThisSection(sectionId) {
            if (this.openSection === true && this.closedSections[sectionId] !== false) {
                this.closedSections = {
                    ...this.closedSections,
                    [sectionId]: false
                }
            }
        },
        toggleAllSections(mode) {
            this.closedSections = Object.keys(this.closedSections).reduce((acc, key) => {
                acc[key] = mode == 'expand' ? false : true
                return acc
            }, {})
        },
        handleMenuBarSectionClick(section) {
            const sectionName = document.getElementById(section.name)

            if (sectionName) {
                sectionName.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        },
        normalizeText(text) {
            if (!text) return ''

            return text
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remover diacríticos (tildes)
        }
    },
    watch: {
        searchValue(newValue, oldValue) {
            if (newValue !== oldValue && newValue.length > 0 && this.sections) {
                const allSections = Object.values(this.checklist.sections)

                const searchResult = allSections.filter((section) => {
                    const normalizedSectionName = this.normalizeText(section.name)
                    const normalizedSearchValue = this.normalizeText(newValue)

                    return normalizedSectionName.includes(normalizedSearchValue)
                })

                this.searchResult = searchResult
            }
        }
    },
    mounted() {},
    created() {
        for (var k in this.checklist.sections) {
            var section = { ...this.checklist.sections[k] }
            this.closedSections[section.section_id] = false
        }
    }
}
</script>

<style lang="scss" scoped>
.audits-sections-and-items {
    width: 100%;
    position: relative;
    // El scroll del checklist debe vivir en #exam (como en Registros) para que
    // TemplateSections pueda sincronizar el navbar horizontal al scrollear.
    overflow: hidden;
    max-height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
    min-height: 0;

    @media (max-width: 600px) {
        max-height: calc(100vh - 17.5vh);
    }

    @media (min-width: 901px) and (max-width: 1121px) {
        max-height: calc(100vh - 116px);
    }

    &.opened-info {
        max-height: calc(100vh - 144px);

        @media (max-width: 600px) {
            max-height: calc(100vh - 42vh);
        }
    }

    .progress-bar-section {
        width: 100%;
        max-width: 100%;
        height: var(--progress-bar-height);
        position: sticky;
        top: 0;

        @media (max-width: 1023px) {
            padding-right: 0px !important;
            padding-left: 0px !important;

            .progress-bar-container {
                padding-left: 0px !important;
            }
        }

        @media (min-width: 1024px) {
            padding-right: 16px;
        }
    }

    .sections-searchbar-container {
        width: 100%;
        padding-right: 16px;

        @media (min-width: 1024px) {
            padding-left: 8px;
        }

        .search-container {
            padding: 0px !important;
            margin: 0px !important;
        }
    }

    .note-detail {
        // margin-top: 20px;
        width: 100%;
        padding: 0 0 0 0;
        position: relative;
    }

    .template-sections {
        // width: 98.8%;
        height: 47px;
        position: sticky;
        top: -1px;
        z-index: 0;
        background-color: #f4f2ed;
        display: grid;
        place-items: center;
        // padding-right: 8px;

        @media (min-width: 1024px) {
            padding-right: 8px;
        }

        @media (min-width: 1201px) {
            width: 99.5%;
        }

        @media (max-width: 1200px) {
            width: 99.5%;
        }

        @media (max-width: 1100px) {
            width: 99%;
        }

        @media (max-width: 900px) {
            width: 99.4%;
        }

        @media (max-width: 570px) {
            width: 98.5%;
        }

        @media (max-width: 425px) {
            width: 98%;
        }

        .template-sections-container {
            width: 99%;
            max-width: 99%;
            overflow-x: hidden;
        }
    }

    .detail-box {
        $sidebar-size: 40px;
        $collapse-button: 40px;
        $content-padding: 0px;
        $detail-bg: $default-bg-color;
        $detail-text-color: $main-dark;
        padding: 0;
        padding-left: $content-padding;
        width: 100%;
        flex: 1;
        min-height: 0;
        overflow: hidden;
        background-color: $detail-bg;

        --progress-bar-height: 32px;

        @media (min-width: 768px) {
            --progress-bar-height: 52px;
        }

        &.with-search-bar {
            height: 56vh !important;

            @media (min-width: 769px) {
                height: 65vh !important;
            }

            @media (min-width: 1025px) {
                height: 54vh !important;
            }
        }

        .exam {
            @include border-radius($default-border-radius);
            padding: 8px 0;
            height: 100%;
            overflow: auto;

            .minimize {
                opacity: 0.85;
                margin-bottom: 0px;
                margin-top: 0px;

                &:hover {
                    opacity: 1;
                }
            }

            .get-last-values {
                text-align: center;

                .message {
                    @include interaction();
                    @include border-radius($default-border-radius);
                    @include background($color: $main-t80, $image: img('download_primary.svg'), $position: right 12px center, $size: 22px);
                    @include font-size(m);
                    font-family: $text;
                    color: $main-dark;
                    padding: 12px 42px 12px 12px;
                    display: inline-block;

                    @include bgHover($main-t80);
                }
            }

            .section {
                $section-score-width: 80px;
                padding: 16px 8px;
                // width: 94%;
                // margin: 12px auto;
                width: 100%;

                @media (max-width: 767px) {
                    padding: 8px 0px;
                }

                .section-progress-indicator {
                    position: relative;
                    background-color: white;
                    width: 40px;
                    height: 40px;
                    border-radius: 4px;
                    display: grid;
                    place-items: center;

                    .icon-container {
                        width: 24px;
                        height: 24px;
                        display: grid;
                        place-items: center;
                        border-radius: 50%;

                        .i-andy-menu-checklist {
                            color: $done;
                        }

                        .i-andy-circle-exclamation {
                            color: #ffcd1a;
                        }
                    }
                }

                .section-title {
                    @include font-size(s);
                    font-family: $text;
                    color: $main-dark;
                    cursor: pointer;

                    // width: calc(100% - #{$section-score-width});
                    // margin-bottom: 4px;

                    display: flex;
                    // align-items: center;
                    align-items: flex-start;
                    gap: 8px;

                    .section-button-container {
                        height: 56px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .section-button {
                        @include background($color: $main-t80, $image: img('less_primary.svg'), $size: 10px);
                        @include border-radius(2px);
                        width: 18px;
                        height: 18px;
                        padding: 4px;
                        cursor: pointer;
                    }
                }

                .section-name-container {
                    width: 100%;
                    display: flex;
                    flex-direction: column;

                    .see-more-box {
                        width: 100%;
                        height: 60px;
                        background-color: white;
                        padding: 8px;

                        border-bottom-left-radius: 4px;
                        border-bottom-right-radius: 4px;

                        display: flex;
                        justify-content: flex-end;
                    }
                }

                .section-name {
                    width: 100%;
                    background-color: #ede7db;
                    padding: 8px;
                    text-align: center;
                    border-radius: 4px;
                    min-height: 56px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;

                    .section-name-text {
                        font-size: 18px;
                        font-family: $text-bold;
                        text-transform: uppercase;
                        max-width: 100%;
                        width: 100%;
                        text-align: center;
                    }
                }

                .section-right-buttons-container {
                    display: flex;
                    width: fit-content;
                    gap: 8px;

                    .see-more-button {
                        background-color: transparent;
                        border: none;
                        cursor: pointer;
                        width: 32px;

                        .i-andy-more {
                            color: #727477;
                        }
                    }
                }

                .section-score {
                    display: inline-block;
                    width: $section-score-width;
                    text-align: right;

                    .points {
                        @include font-size(l);
                        font-family: $conden-bold;
                        color: $main;
                    }

                    .total {
                        @include font-size(sm);
                        font-family: $conden-bold;
                        color: $neutro-s90;
                        padding-right: 6px;
                    }

                    .percentage {
                        font-family: $text-medium;
                        color: $main;
                        background-color: #fff;
                        padding: 6px;
                        border-radius: 4px;
                        @include font-size(xs);
                        position: relative;
                        // top: -2px;
                    }
                }

                .section-content {
                    @include display-flex();
                    @include flex-direction(row);
                    @include flex-wrap(wrap);
                    @include border-radius($default-border-radius);

                    display: flex;
                    flex-direction: column;
                    gap: 16px;

                    @media (min-width: 901px) {
                        gap: 20px;
                        padding-top: 8px;
                    }

                    .action-plan-section {
                        width: calc(100% - 48px);
                        margin-left: 48px;
                        padding-top: 24px;

                        @media (max-width: 767px) {
                            width: calc(100% - 16px);
                            margin-left: 16px;
                        }

                        // padding-bottom: 24px;

                        .title-and-button {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding-bottom: 4px;
                        }

                        .action-plan-title {
                            font-size: 18px;
                        }

                        .show-hide {
                            display: flex;
                            gap: 4px;
                            align-items: center;
                            background-color: none;

                            .label {
                                color: #374673;
                                font-size: 18px;
                                font-weight: bold;
                            }
                        }

                        .show-hide:hover {
                            background-color: transparent;
                        }

                        .action-plan-items-container {
                            padding-left: 16px;
                        }
                    }

                    .question-score {
                        @include display-inline-flex();
                        // @include justify-content();
                        @include align-items();
                        @include font-size(m);
                        margin: 20px auto;
                        font-family: $text;
                        color: $main;
                        width: 80px;
                        height: 50px;
                    }

                    .subitems {
                        width: calc(100% - 48px);
                        margin-left: 48px;
                        margin-top: 8px;
                        padding-right: 0px;

                        @media (max-width: 767px) {
                            width: calc(100% - 16px);
                            margin-left: 16px;
                        }
                    }
                }

                .section-total {
                    width: 100%;
                    text-align: right;

                    .text {
                        @include font-size(sm);
                        font-family: $text-bold;
                        color: $main-dark;
                    }

                    .total {
                        @include styScore();
                        @include font-size(ml);
                        font-family: $text-bold;
                        color: $main;
                        text-align: center;
                        display: inline-block;
                        width: 80px;
                        padding: 6px;
                    }
                }

                &.closed {
                    //height: 50px;
                    // margin: 12px auto;

                    .section-button {
                        @include background($color: $main-t80, $image: img('more_primary.svg'), $size: 10px);
                    }

                    .section-content {
                        visibility: hidden;
                        display: none;
                        height: 0;
                        opacity: 0;

                        // .question, .question-score {
                        //   display: none;
                        // }
                    }
                }
            }
        }
    }

    .desktop {
        .detail-box {
            // height: calc(100% - 160px);
            // &.with-sections {
            //     height: calc(100% - 200px);

            //     &.opened-info {
            //         height: calc(100% - 255px);
            //     }
            // }

            // &.opened-info {
            //     height: calc(100% - 195px);
            // }

            .section {
                .section-title {
                    @include font-size(ml);
                }
            }

            .section-score {
                .percentage {
                    @include font-size(m, $important: true);
                }
            }
        }
    }

    .landscape {
        // Calcular la altura de todo el contenido top que es fijo para el listado
        .detail-box {
            // height: calc(100% - 140px);

            // &.with-sections {
            //     height: calc(100% - 200px);

            //     &.opened-info {
            //         height: calc(100% - 245px);
            //     }
            // }

            // &.opened-info {
            //     height: calc(100% - 175px);
            // }
            .section {
                .section-title {
                    @include font-size(ml);
                }
            }

            .section-score {
                .percentage {
                    @include font-size(m, $important: true);
                }
            }
        }
    }

    .exam {
        .section {
            width: 100%;

            $section-score-width: 80px;
            // margin: 8px auto;
            padding: 8px;

            .section-title {
                // width: calc(100% - #{$section-score-width});
                @include font-size(xs);
                line-height: normal;
                width: 100%;

                &.expand-full {
                    width: 100%;
                }
            }

            .section-score {
                width: $section-score-width;
                justify-self: end;
            }
        }

        .justify-description,
        .check-available {
            padding: 8px 20px 8px 50px;
            background-size: 25px;
            background-position: left 10px center;

            .message {
                @include font-size(xs);
                line-height: 1rem;
                width: calc(100% - 100px);
            }

            .action {
                width: 100px;
                @include font-size(xs);
                line-height: 0.75rem;
            }
        }

        .check-available {
            .message {
                width: 100%;
                max-width: 100%;
            }
        }

        .get-last-values {
            .message {
                @include font-size(xs);
                line-height: normal;
            }
        }
    }

    @media (max-width: 767px) {
        .detail-box {
            // padding-top: 60px !important;
            background: transparent;
            // height: calc(100% - 175px);

            // &.opened-info {
            //     height: calc(100% - 220px);
            // }

            .message {
                padding-left: 10px;
            }

            &.not-saved {
                padding-bottom: 0px;
            }

            &.with-sections {
                padding-bottom: 0px;
            }
        }
    }

    .apply-button {
        font-size: 0.875rem;
        color: black;
        width: fit-content;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 8px;
        border-radius: 4px;

        &.no-apply {
            line-height: 1.1;
        }

        &.apply {
            gap: 4px;
        }
    }

    .apply-button:hover {
        background-color: #f3f3f3;
    }
}
</style>
